/**
 * What to suggest when the keys are handed back.
 *
 * <p>🔑 The workshop is telling the customer when to come back, and it is a
 * confirmation rather than a calculation: the default is filled in, staff change it if
 * this car is different. A blank box gets skipped, and then nobody is told anything.
 *
 * <h2>🔴 This is a REMINDER, not an appointment</h2>
 *
 * It decides roughly when to nudge a customer; they then book, and the booking is the
 * thing with a time on it. A day either way changes nothing, which is why this is a
 * plain `LocalDate` on a `DATE` column with no instant, no zone and no conversion
 * anywhere in its path.
 *
 * <p>⚠️ Do not "fix" that by plumbing the organisation's timezone through here. It
 * would add a conversion to a value that has no time of day to convert, and the one
 * place a workshop's zone genuinely matters — the TZID on a booking's calendar invite,
 * where a customer is told to turn up at 9am — is a different value on a different path.
 *
 * <p>The local date parts below are still built by hand rather than with
 * `toISOString()`, but only so the box does not read a day out. That is a display
 * concern, not a correctness one.
 *
 * <h2>The two numbers</h2>
 *
 * ⚠️ A house default, NOT the customer's logbook. A logbook service falls due on
 * distance or time, whichever comes first — hence both — but the real intervals live in
 * the workshop's service profiles and vary by vehicle. `ServiceDueCalculator` is what
 * knows them; this is the sensible thing to put in front of somebody at the counter.
 */

/** Distance until the next service, when nobody says otherwise. */
export const DEFAULT_NEXT_SERVICE_KM = 10000;

/** Months until the next service, when nobody says otherwise. */
export const DEFAULT_NEXT_SERVICE_MONTHS = 3;

/**
 * @param odometerKm the reading the car left on, or null when it was not recorded
 * @param today      injected so the suggestion is testable without a calendar
 * @returns `{ nextServiceDue: 'YYYY-MM-DD', nextServiceKm: number|null }`
 */
export const suggestNextService = (odometerKm, today = new Date()) => {
    const due = new Date(today.getFullYear(), today.getMonth() + DEFAULT_NEXT_SERVICE_MONTHS,
        today.getDate());

    // ⚠️ Local date parts, never toISOString(): that converts to UTC first, so at 9am
    // in Melbourne it returns YESTERDAY. Nothing breaks — it is a reminder — but a box
    // that reads a day out is the kind of thing staff stop trusting.
    const pad = (n) => String(n).padStart(2, '0');
    const nextServiceDue =
        `${due.getFullYear()}-${pad(due.getMonth() + 1)}-${pad(due.getDate())}`;

    // No reading means no distance to add to. A date on its own is a valid answer —
    // the service falls due on whichever comes first, so one of the two is enough.
    const nextServiceKm = Number.isFinite(odometerKm) && odometerKm > 0
        ? odometerKm + DEFAULT_NEXT_SERVICE_KM
        : null;

    return { nextServiceDue, nextServiceKm };
};
